import { useMemo, useState } from 'react'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { Select } from '../../platform/primitives/Select'
import type { Category } from './categoryTree'
import { defaultTarget, deleteTargets } from './deleteTargets'
import { CategoriesError, useDeleteCategory } from './useCategories'

const UNCATEGORIZED = ''

/**
 * Confirm a category delete and choose where its transactions go (#285,
 * M11-R3). The picker is always shown; "Uncategorized" omits `reassign_to`.
 *
 * The body sits inside `ConfirmDialog`'s `<p>`, so it is phrasing content
 * only — spans, a label, a select.
 */
export function CategoryDeleteDialog({
  category,
  categories,
  onClose,
  onDeleted,
}: {
  category: Category
  categories: readonly Category[]
  onClose: () => void
  /**
   * Called once the delete has succeeded and the list has refetched, with the
   * label of the category the transactions moved to (`undefined` when they
   * became uncategorised). The ROUTE announces it (#376): this dialog stays
   * free of `useToast()` so it needs no provider.
   */
  onDeleted: (movedTo: string | undefined) => void
}) {
  const remove = useDeleteCategory()
  const targets = useMemo(() => deleteTargets(category, categories), [category, categories])
  const [choice, setChoice] = useState<string>(defaultTarget(category, targets) ?? UNCATEGORIZED)

  const nameById = useMemo(() => new Map(categories.map((c) => [c.id, c.name])), [categories])
  // A sub-category is labelled with its parent so two "Other"s can be told apart.
  const labelOf = (c: Category) =>
    c.parent_id != null && nameById.has(c.parent_id) ? `${nameById.get(c.parent_id)} › ${c.name}` : c.name
  const options = targets
    .map((c) => ({ id: c.id, label: labelOf(c) }))
    .sort((a, b) => a.label.localeCompare(b.label))
  const selected = targets.find((t) => t.id === choice)

  return (
    <ConfirmDialog
      open
      mode="delete_category"
      destructive
      title={`Delete ${category.name}?`}
      body={
        <>
          <span className="categories__delete-line">
            {selected === undefined
              ? 'Its transactions become uncategorized and appear in your review queue, and stay open to your rules.'
              : `Its transactions and recurring items are moved to ${labelOf(selected)} and locked, so rules will not change them.`}
          </span>
          <span className="categories__delete-line">
            Any sub-categories move to the top level.
          </span>
          <label className="categories__delete-line" htmlFor="category-delete-target">
            Move its transactions to
          </label>
          <Select
            id="category-delete-target"
            label="Move its transactions to"
            value={choice}
            disabled={remove.isPending}
            onChange={(e) => setChoice(e.target.value)}
          >
            <option value={UNCATEGORIZED}>Uncategorized</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Select>
        </>
      }
      confirmLabel="Delete category"
      pendingLabel="Deleting…"
      errorFor={(thrown) => {
        const status = thrown instanceof CategoriesError ? thrown.status : undefined
        if (status === 404) return 'This category cannot be deleted.'
        if (status === 422) return 'Those transactions cannot be moved to that category. Choose another and try again.'
        return 'The category could not be deleted.'
      }}
      onCancel={onClose}
      onConfirm={async () => {
        await remove.mutateAsync({ category, reassignTo: choice === UNCATEGORIZED ? null : choice })
        onDeleted(selected === undefined ? undefined : labelOf(selected))
        onClose()
      }}
    />
  )
}
